import type { EstimateResponse } from '@rheora/enrollment-contract';

import { useTranslation } from '@fluxora/i18n';

import {
  activeCurveIndex,
  formatEffectiveRateMinor,
  formatMoneyWireToDollars,
} from '../services/estimate.service';

/**
 * The effective-rate curve at the three §25.475 reference usages (500/1000/2000
 * kWh), with the tier nearest the premise's usage highlighted (E-SF04). Shows the
 * customer how the effective ¢/kWh moves with usage and where their premise sits.
 *
 * Pure presentation — the nearest-tier selection + all formatting come from
 * `estimate.service`.
 */
export interface EffectiveRateCurveProps {
  readonly estimate: EstimateResponse;
}

export function EffectiveRateCurve({ estimate }: EffectiveRateCurveProps): JSX.Element {
  const { t } = useTranslation();
  const active = activeCurveIndex(estimate);

  return (
    <div className="flex flex-col gap-2" data-testid="effective-rate-curve">
      <span className="text-xs uppercase tracking-wide text-foreground/60">
        {t('shop.estimate.curve.label')}
      </span>
      <ul className="flex flex-col divide-y divide-border rounded-md border border-border">
        {estimate.curve.map((point, index) => (
          <li
            key={point.usageKwh}
            aria-current={index === active ? 'true' : undefined}
            className={
              index === active
                ? 'flex items-center justify-between bg-primary/10 px-3 py-2'
                : 'flex items-center justify-between px-3 py-2'
            }
          >
            <span className="text-sm text-foreground/80">
              {t('shop.estimate.curve.atUsage', { kwh: point.usageKwh })}
            </span>
            <span className="flex items-baseline gap-2">
              <span className="text-sm font-semibold text-foreground">
                {formatEffectiveRateMinor(point.effectiveRateMinor)}
                <span className="ml-0.5 text-xs font-normal text-foreground/60">
                  {t('shop.estimate.curve.unit')}
                </span>
              </span>
              <span className="text-xs text-foreground/60">
                {formatMoneyWireToDollars(point.monthlyBill)}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
